import { makeAutoObservable } from 'mobx';
import toastStore from "../../../store/toast-store";
import compagnyModel, {
    CompaniesResponse,
    CompanyAddFormData,
    CompanyEditFormData
} from "../../../model/professional/compagny-model";
import {HTTP_STATUS_CODE} from "../../../utils/UHttp";
import companyStore from "../../../store/company-store";

// Types pour les différentes vues
export type CompanyViewType = 'list' | 'add' | 'edit' | 'details' | 'view' | 'collaborators';

export class CompanyViewModel {

    errorMessage: string = "";
    successMessage: string = "";
    isLoading: boolean = false;

    // États pour gérer les différentes vues et actions
    currentView: CompanyViewType = 'list';
    isDeleteModalOpen: boolean = false;
    isInviteModalOpen: boolean = false;
    isCollaboratorsListModalOpen: boolean = false;
    invitationSent: boolean = false;
    invitedEmail: string = '';

    // États pour les clients de l'entreprise
    companyClients: any[] = [];
    isLoadingClients: boolean = false;

    private static instance: CompanyViewModel;

    private constructor() {
        makeAutoObservable(this);
    }

    public static getInstance(): CompanyViewModel {
        if (!CompanyViewModel.instance) {
            CompanyViewModel.instance = new CompanyViewModel();
        }
        return CompanyViewModel.instance;
    }

    // Setters pour les états
    setIsLoadind(isLoading: boolean) {
        this.isLoading = isLoading;
    }

    setErrorMessage(message: string) {
        this.errorMessage = message;
    }

    setSuccessMessage(message: string) {
        this.successMessage = message;
    }

    setCurrentView(view: CompanyViewType) {
        this.currentView = view;
    }

    setIsDeleteModalOpen(isOpen: boolean) {
        this.isDeleteModalOpen = isOpen;
    }

    setIsInviteModalOpen(isOpen: boolean) {
        this.isInviteModalOpen = isOpen;
    }

    setIsCollaboratorsListModalOpen(isOpen: boolean) {
        this.isCollaboratorsListModalOpen = isOpen;
    }

    setInvitationSent(sent: boolean) {
        this.invitationSent = sent;
    }

    setInvitedEmail(email: string) {
        this.invitedEmail = email;
    }

    // Gestionnaires d'événements pour les actions sur les entreprises
    handleAddCompany = () => {
        this.setCurrentView('add');
    };

    handleEditCompany = (companyId: string) => {
        companyStore.setCompanyById(companyId);
        this.setCurrentView('edit');
    };

    handleViewCompanyDetails = (companyId: string) => {
        companyStore.setCompanyById(companyId);
        this.setCurrentView('details');
    };

    handleViewCompany = (companyId: string) => {
        companyStore.setCompanyById(companyId);
        this.setCurrentView('view');
    };

    handleViewCollaborators = (companyId: string) => {
        companyStore.setCompanyById(companyId);
        this.setCurrentView('collaborators');
    };

    handleDeleteCompany = (companyId: string) => {
        companyStore.setCompanyById(companyId);
        this.setIsDeleteModalOpen(true);
    };


    handleCancelDelete = () => {
        this.setIsDeleteModalOpen(false);
    };

    handleFormSubmit = (data: any) => {
        // Ici, vous feriez normalement un appel API pour créer ou mettre à jour l'entreprise
        console.log('Données du formulaire:', data);
        // this.setCurrentView('list');
    };

    handleFormUpdateSubmit = async (data: any) => {
        // Ici, vous feriez normalement un appel API pour créer ou mettre à jour l'entreprise
        const response = await this.handleUpdateCompany(data);
        if (response) this.setCurrentView('list');
    };

    handleFormCancel = () => {
        this.setCurrentView('list');
    };

    handleBackToList = () => {
        this.setCurrentView('list');
    };

    // Gestionnaires pour le modal d'invitation
    handleOpenInviteModal = () => {
        this.setIsInviteModalOpen(true);
    };

    handleInviteModalClose = () => {
        this.setIsInviteModalOpen(false);
    };

    handleOpenCollaboratorsListModal = () => {
        this.setIsCollaboratorsListModalOpen(true);
    };

    handleCollaboratorsListModalClose = () => {
        this.setIsCollaboratorsListModalOpen(false);
    };

    handleInvite = (email: string, role: 'Admin' | 'Staff') => {
        // Ici, vous feriez normalement un appel API pour envoyer l'invitation
        console.log(`Invitation envoyée à ${email} avec le rôle ${role}`);

        // Mettre à jour les états pour afficher un message de confirmation
        this.setInvitedEmail(email);
        this.setInvitationSent(true);
        this.setIsInviteModalOpen(false);

        // Réinitialiser le message après 5 secondes
        setTimeout(() => {
            this.setInvitationSent(false);
        }, 5000);
    };

    // Méthodes API existantes
    handleGetAllCompaniesByCollaborator = async () => {
        try {
            const response: CompaniesResponse = await compagnyModel.handleGetAllCompaniesByCollaborator();
            if(response.status  === HTTP_STATUS_CODE.OK) {
               companyStore.setCompanies(response.companies);
            }
        } catch (error) {
            console.error("error CompanyViewModel.handleGetAllCompaniesByCollaborator", error);
            toastStore.error('Une erreur est survenue lors de la récupération des entreprises');
        }
    }

    handleNewCompany = async (data: CompanyAddFormData) => {
        try {
            this.setErrorMessage("");
            this.setIsLoadind(true);

            // Étape 1 : Créer l'entreprise (sans les fichiers)
            const response: any = await compagnyModel.handleCreateCompany(data);

            if(response.status === HTTP_STATUS_CODE.CREATED) {
                const companyId = response.company?.id;

                if (!companyId) {
                    this.setErrorMessage('Entreprise créée mais ID manquant');
                    this.setIsLoadind(false);
                    return;
                }

                // Étape 2 : Upload du logo si présent
                if (data.logo) {
                    try {
                        await compagnyModel.handleUpdateCompanyPicture(companyId, data.logo);
                    } catch (logoError) {
                        console.error("Error uploading logo:", logoError);
                        // On continue même si le logo échoue
                    }
                }

                // Étape 3 : Upload des photos de galerie si présentes
                if (data.galleryPhoto && Array.isArray(data.galleryPhoto) && data.galleryPhoto.length > 0) {
                    // Filtrer uniquement les fichiers (pas les strings)
                    const photos = data.galleryPhoto as (File | string)[];
                    const photoFiles = photos.filter((photo): photo is File => {
                        return photo instanceof File;
                    });

                    if (photoFiles.length > 0) {
                        try {
                            await compagnyModel.handleUpdateGalleryPhotos(companyId, photoFiles);
                        } catch (galleryError) {
                            console.error("Error uploading gallery photos:", galleryError);
                            // On continue même si la galerie échoue
                        }
                    }
                }

                this.setSuccessMessage(response.message);
                this.setIsLoadind(false);

                // Recharger la page après 1.5 seconde
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                this.setErrorMessage(response.message);
                this.setIsLoadind(false);
            }

        } catch (error) {
            console.error("error CompanyViewModel.handleNewCompany", error);
            this.setErrorMessage('Une erreur est survenue lors de la creation');
            this.setIsLoadind(false);
        }
    }

    handleConfirmDelete = async () => {
        // Ici, vous feriez normalement un appel API pour supprimer l'entreprise
        const company = companyStore.getCompanyById;

        try {
            if (!company?.id) return
            this.setErrorMessage("");
            const response: any = await compagnyModel.handleDeleteCompany(company.id);

            if(response.status === HTTP_STATUS_CODE.OK) {
                this.setSuccessMessage(response.message);
                toastStore.success(response.message)
                // Recharger la page après 1.5 seconde
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                this.setErrorMessage(response.message);
            }
        } catch (error) {
            console.error("error CompanyViewModel.handleConfirmDelete", error);
            this.setErrorMessage('Une erreur est survenue lors de la suppression');
        }
    };

    handleUpdateCompany = async (data: CompanyEditFormData) => {
        try {
            this.setErrorMessage("");
            this.setIsLoadind(true);

            // Étape 1 : Mettre à jour les données textuelles (sans les fichiers)
            // On exclut logo et galleryPhoto car ils sont gérés séparément
            const { logo, galleryPhoto, ...textData } = data;
            const updateData = { ...textData, galleryPhoto: null, logo: null };
            const response: any = await compagnyModel.handleUpdateCompany(updateData);

            if(response.status === HTTP_STATUS_CODE.OK) {
                const companyId = data.id;

                // Étape 2 : Upload du logo si un nouveau fichier est fourni
                if (logo && logo instanceof File) {
                    try {
                        await compagnyModel.handleUpdateCompanyPicture(companyId, logo);
                    } catch (logoError) {
                        console.error("Error uploading logo:", logoError);
                        // On continue même si le logo échoue
                    }
                }

                // Étape 3 : Gérer les suppressions de photos de galerie
                const editFormViewModel = await import('./company-edit-form-view-model');
                const vm = editFormViewModel.default;

                // Supprimer les photos marquées pour suppression (en ordre décroissant pour éviter les problèmes d'index)
                if (vm.deletedPhotoIndices && vm.deletedPhotoIndices.length > 0) {
                    const sortedIndices = [...vm.deletedPhotoIndices].sort((a, b) => b - a);
                    for (const photoIndex of sortedIndices) {
                        try {
                            await compagnyModel.handleUpdateGalleryPhotos(companyId, [], photoIndex, true);
                        } catch (deleteError) {
                            console.error(`Error deleting gallery photo at index ${photoIndex}:`, deleteError);
                            // On continue même si la suppression échoue
                        }
                    }
                }

                // Étape 4 : Upload des nouvelles photos de galerie
                if (vm.galleryPhotoFiles && vm.galleryPhotoFiles.length > 0) {
                    try {
                        await compagnyModel.handleUpdateGalleryPhotos(companyId, vm.galleryPhotoFiles);
                    } catch (galleryError) {
                        console.error("Error uploading gallery photos:", galleryError);
                        // On continue même si la galerie échoue
                    }
                }

                toastStore.success(response.message);
                this.setIsLoadind(false);

                setTimeout(() => {
                    window.location.reload();
                }, 1500);

                return true;
            } else {
                toastStore.error(response.message);
                this.setIsLoadind(false);
                return false;
            }
        } catch (error) {
            console.error("error CompanyViewModel.handleUpdateCompany", error);
            this.setErrorMessage('Une erreur est survenue lors de la mise à jour');
            this.setIsLoadind(false);
            return false;
        }
    }

    /**
     * Charge les clients qui ont réservé des services de l'entreprise
     */
    handleGetCompanyClients = async (companyId: string) => {
        try {
            this.isLoadingClients = true;
            const response = await compagnyModel.getCompanyClients(companyId);

            if (response.success && response.data) {
                this.companyClients = response.data;
            } else {
                this.companyClients = [];
                console.error('Error loading company clients:', response.message);
            }
        } catch (error) {
            console.error("error CompanyViewModel.handleGetCompanyClients", error);
            this.companyClients = [];
        } finally {
            this.isLoadingClients = false;
        }
    }
}

export default CompanyViewModel.getInstance()